import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';
import CustomDropdown from '../CustomDropdown';
import CustomDatePicker from '../CustomDatePicker';

const BookingManagement = () => {
  const { adminUser } = useAdmin();
  const [bookings, setBookings] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filters, setFilters] = useState({
    date: '',
    courtId: '',
    status: '',
    search: '',
    limit: '50'
  });
  
  // Cancel booking state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelData, setCancelData] = useState({
    reason: '',
    refundTokens: true
  });
  const [cancelling, setCancelling] = useState(false);
  const [refundingId, setRefundingId] = useState(null);


  // Load initial data on component mount
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency array - only run on mount

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        date: filters.date,
        courtId: filters.courtId,
        status: filters.status,
        search: filters.search,
        limit: filters.limit
      };
      const [bookingsData, courtsData] = await Promise.all([
        adminApiService.getBookings(adminUser.idToken, params),
        adminApiService.getCourts(adminUser.idToken)
      ]);
      setBookings(bookingsData.bookings || bookingsData); // Handle both new and old API response formats
      setCourts(courtsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearchSubmit = () => {
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      date: '',
      courtId: '',
      status: '',
      search: '',
      limit: '50'
    });
  };

  const handleCancelBooking = (booking) => {
    setSelectedBooking(booking);
    setCancelData({
      reason: '',
      refundTokens: booking.paymentStatus !== 'pending'
    });
    setShowCancelModal(true);
  };

  const handleCancelSubmit = async () => {
    if (!selectedBooking) return;
    
    try {
      setCancelling(true);
      setError(null);
      
      const result = await adminApiService.cancelBooking(
        adminUser.idToken,
        selectedBooking.id,
        cancelData
      );
      
      let successMessage = `ยกเลิกการจองสำเร็จ: ${result.message}`;
      if (result.refundInfo && result.refundInfo.method === 'tokens') {
        successMessage += ` (คืนตั๋ว ${result.refundInfo.amount} ตั๋ว)`;
      }
      setSuccess(successMessage);
      setShowCancelModal(false);
      setSelectedBooking(null);
      
      // Refresh bookings list
      await fetchData();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  // A QR payment that couldn't confirm its booking (see API confirmBookingPayment).
  // Staff refund it outside the app, then record it here so it isn't refunded twice.
  const refundReasonText = {
    slot_taken: 'ชำระหลังหมดเวลาจอง และช่วงเวลานี้มีผู้จองแล้ว',
    booking_cancelled: 'ชำระหลังการจองถูกยกเลิก',
    slot_passed: 'ชำระหลังเวลาจองผ่านไปแล้ว',
    already_paid: 'ชำระซ้ำ (การจองนี้ชำระแล้ว)',
    booking_missing: 'ไม่พบการจอง',
  };

  const handleRefundDone = async (booking) => {
    const note = window.prompt(
      `ยืนยันว่าคืนเงิน ฿${Number(booking.refund.amount).toLocaleString()} ให้ลูกค้าแล้ว?\nบันทึกวิธีคืนเงิน (เช่น โอนธนาคาร 21/10):`,
      ''
    );
    if (note === null) return;
    try {
      setRefundingId(booking.id);
      setError(null);
      await adminApiService.markRefundDone(adminUser.idToken, booking.id, note);
      setSuccess(`บันทึกการคืนเงิน ฿${Number(booking.refund.amount).toLocaleString()} สำเร็จ`);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefundingId(null);
    }
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    setSelectedBooking(null);
    setCancelData({
      reason: '',
      refundTokens: true
    });
    setError(null);
  };

  const getCourtName = (courtId) => {
    const court = courts.find(c => c.id === courtId);
    return court ? court.name : 'ไม่ทราบสนาม';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'รอดำเนินการ' },
      confirmed: { color: 'bg-green-100 text-green-800', text: 'ยืนยันแล้ว' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'ยกเลิกแล้ว' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'รอชำระ' },
      paid: { color: 'bg-green-100 text-green-800', text: 'ชำระแล้ว' },
      failed: { color: 'bg-red-100 text-red-800', text: 'ล้มเหลว' },
      cancelled: { color: 'bg-gray-100 text-gray-700', text: 'ยกเลิก' }
    };
    const config = statusConfig[paymentStatus] || { color: 'bg-gray-100 text-gray-800', text: paymentStatus };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getPaymentMethodBadge = (payment) => {
    if (!payment || !payment.paymentMethod) {
      return null;
    }
    
    const method = payment.paymentMethod === 'tokens' ? 'tokens' : 'cash';
    const config = {
      tokens: { color: 'bg-purple-100 text-purple-800', text: 'ใช้เหรียญ' },
      cash: { color: 'bg-blue-100 text-blue-800', text: 'เงินสด' }
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config[method].color}`}>
        {config[method].text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">การจอง</h2>
        <div className="text-sm text-gray-500">
          รวม: {bookings.length} การจอง
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">ตัวกรอง</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">ค้นหา</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="ค้นหาด้วยรหัสการจองหรือชื่อผู้ใช้"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">วันที่</label>
            <CustomDatePicker
              value={filters.date}
              onChange={(dateStr) => handleFilterChange('date', dateStr)}
              placeholder="ทุกวันที่"
              allowClear
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">สนาม</label>
            <CustomDropdown
              value={filters.courtId}
              onChange={(val) => handleFilterChange('courtId', val)}
              placeholder="ทุกสนาม"
              options={[{ value: '', label: 'ทุกสนาม' }, ...courts.map((court) => ({ value: court.id, label: court.name }))]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">สถานะ</label>
            <CustomDropdown
              value={filters.status}
              onChange={(val) => handleFilterChange('status', val)}
              placeholder="ทุกสถานะ"
              options={[
                { value: '', label: 'ทุกสถานะ' },
                { value: 'pending', label: 'รอดำเนินการ' },
                { value: 'confirmed', label: 'ยืนยันแล้ว' },
                { value: 'cancelled', label: 'ยกเลิกแล้ว' },
              ]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">จำนวนรายการ</label>
            <CustomDropdown
              value={filters.limit}
              onChange={(val) => handleFilterChange('limit', val)}
              options={[
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={clearFilters}
            className="text-sm text-gray-600 hover:text-gray-800 underline"
          >
            ล้างตัวกรองทั้งหมด
          </button>
          <button
            onClick={handleSearchSubmit}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            🔍 ค้นหา
          </button>
        </div>
      </div>

      {/* Bookings List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {bookings.map((booking) => (
            <li key={booking.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="text-2xl">📅</span>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            {getCourtName(booking.courtId)}
                          </p>
                          <span className="text-sm text-gray-500">
                            {formatDate(booking.date)} เวลา {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                          </span>
                        </div>
                        <div className="flex items-center mt-1 space-x-2">
                          <span className="text-sm text-gray-600">
                            {booking.displayName} • {booking.duration}นาที • ฿{booking.totalCost}
                          </span>
                          {getStatusBadge(booking.status)}
                          {getPaymentStatusBadge(booking.paymentStatus)}
                          {getPaymentMethodBadge(booking.payment)}
                        </div>
                        {booking.bookingName && (
                          <div className="mt-1">
                            <span className="text-xs text-gray-500">ชื่อที่จอง: {booking.bookingName}</span>
                          </div>
                        )}
                        <div className="mt-1">
                          <span className="text-xs text-gray-500">ผู้ใช้: {booking.userName}</span>
                        </div>
                        <div className="mt-1">
                          <span className="text-xs text-gray-500">รหัสการจอง: {booking.id}</span>
                        </div>
                        <div className="mt-1">
                          <span className="text-xs text-gray-500">
                            สร้างเมื่อ: {formatDateTime(booking.createdAt)}
                          </span>
                        </div>
                        {booking.refund && (
                          <div className={`mt-2 rounded-md border px-3 py-2 text-xs ${booking.refund.status === 'due' ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-green-200 bg-green-50 text-green-800'}`}>
                            <p className="font-semibold">
                              {booking.refund.status === 'due' ? '⚠️ ต้องคืนเงินลูกค้า' : '✓ คืนเงินแล้ว'} ฿{Number(booking.refund.amount).toLocaleString()} · {refundReasonText[booking.refund.reason] || booking.refund.reason}
                            </p>
                            {(booking.refund.payments || []).map((p) => (
                              <p key={p.paymentId} className="mt-0.5 font-mono">
                                {p.paymentId} · ฿{Number(p.amount).toLocaleString()} · ChillPay {p.transactionId || '-'} · ธนาคาร {p.bankRef || '-'} · {formatDateTime(p.paidAt)}
                              </p>
                            ))}
                            {booking.refund.conflictBookingId && (
                              <p className="mt-0.5">ช่วงเวลานี้ถูกจองโดย: {booking.refund.conflictBookingId}</p>
                            )}
                            {booking.refund.status === 'refunded' ? (
                              <p className="mt-0.5">
                                โดย {booking.refund.refundedBy} · {formatDateTime(booking.refund.refundedAt)}{booking.refund.note ? ` · ${booking.refund.note}` : ''}
                              </p>
                            ) : (
                              <button
                                onClick={() => handleRefundDone(booking)}
                                disabled={refundingId === booking.id}
                                className="mt-2 inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                              >
                                {refundingId === booking.id ? 'กำลังบันทึก…' : 'ทำเครื่องหมายว่าคืนเงินแล้ว'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      {booking.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancelBooking(booking)}
                          className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <span className="mr-1">❌</span>
                          ยกเลิก
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {bookings.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            ไม่พบการจองที่ตรงกับตัวกรองปัจจุบัน
          </div>
        )}
      </div>

      {/* Cancel Booking Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <span className="text-red-600 text-2xl mr-3">⚠️</span>
              <h3 className="text-lg font-medium text-gray-900">ยกเลิกการจอง</h3>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">รายละเอียดการจอง</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="font-medium">สนาม:</span> {getCourtName(selectedBooking.courtId)}</p>
                <p><span className="font-medium">วันที่:</span> {formatDate(selectedBooking.date)}</p>
                <p><span className="font-medium">เวลา:</span> {formatTime(selectedBooking.startTime)} - {formatTime(selectedBooking.endTime)}</p>
                <p><span className="font-medium">ผู้จอง:</span> {selectedBooking.contactData?.nickname}</p>
                <p><span className="font-medium">เบอร์ติดต่อ:</span> {selectedBooking.contactData?.mobilePhone}</p>
                <p><span className="font-medium">ค่าใช้จ่าย:</span> {
                  selectedBooking.payment?.paymentMethod === 'tokens' 
                    ? `${selectedBooking.payment?.tokensUsed} เหรียญ`
                    : `฿${selectedBooking.payment?.amount || 0}`
                }</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เหตุผลในการยกเลิก *
              </label>
              <textarea
                value={cancelData.reason}
                onChange={(e) => setCancelData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="กรุณาระบุเหตุผลในการยกเลิกการจอง"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                required
              />
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">การคืนเงิน</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={cancelData.refundTokens}
                    onChange={(e) => setCancelData(prev => ({ ...prev, refundTokens: e.target.checked }))}
                    disabled={selectedBooking?.paymentStatus === 'pending'}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className={`ml-2 text-sm ${selectedBooking?.paymentStatus === 'pending' ? 'text-gray-400' : 'text-gray-700'}`}>
                    คืนเหรียญ (Tokens)
                  </span>
                </label>
                {selectedBooking && selectedBooking.paymentStatus !== 'pending' && (
                  <div className="ml-6 text-xs text-gray-500">
                    จำนวนตั๋วที่จะคืน: {Math.round(selectedBooking.duration / 60)} ตั๋ว (1 ตั๋ว = 1 ชั่วโมง)
                  </div>
                )}
                {selectedBooking && selectedBooking.paymentStatus === 'pending' && (
                  <div className="ml-6 text-xs text-gray-400">
                    ไม่สามารถคืนตั๋วได้ เนื่องจากยังไม่ได้ชำระเงิน
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelModalClose}
                disabled={cancelling}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleCancelSubmit}
                disabled={cancelling || !cancelData.reason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {cancelling ? 'กำลังยกเลิก...' : 'ยืนยันการยกเลิก'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManagement; 